import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowLeft, ArrowRight, ChevronRight, ChevronLeft, Plus, Minus, Sparkles, Heart, Share2, 
  Gift, Send, X, CheckCircle, Truck, Ruler, Search, User, Loader2, AlertTriangle, Link as LinkIcon,
  Clock // 🟢 ADDED CLOCK FOR PRE-ORDER BADGE
} from 'lucide-react';
import { Product } from '../../types';
import { formatCurrency } from '../../utils/formatters';

// 🟢 FIREBASE IMPORTS
import { db, auth } from '../../services/firebase';
import { collection, getDocs, query, where, addDoc, setDoc, doc, serverTimestamp, increment, onSnapshot, getDoc } from 'firebase/firestore';

interface ProductDetailModalProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (product: Product, size: string, color: string, quantity: number) => void;
  onBuyNow: (product: Product, size: string, color: string, quantity: number) => void;
  isWishlisted: boolean;
  onToggleWishlist: () => void;
  onViewBrand: (brandId: string) => void;
  onOpenAI: (product: Product) => void;
  onOpenSizeGuide: () => void;
  onShareToDM: (product: Product, targetUser?: any) => void;
  onRequestGift: (product: Product, targetUser?: any) => void;
  onSendGift: (product: Product, targetUser?: any) => void;
}

export const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  product, isOpen, onClose, onAddToCart, onBuyNow,
  isWishlisted, onToggleWishlist, onViewBrand, onOpenAI, onOpenSizeGuide,
  onShareToDM, onRequestGift, onSendGift
}) => {
  // 🟢 LIVE PRODUCT STATE
  const [liveProduct, setLiveProduct] = useState<any>(product);
  
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // 🟢 UI VALIDATION STATE
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isShaking, setIsShaking] = useState(false);

  // 🟢 UNIFIED SOCIAL ACTION STATE
  const [actionMode, setActionMode] = useState<'dm' | 'request' | 'send_gift' | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionSuccess, setActionSuccess] = useState(false);
  const [isTransmitting, setIsTransmitting] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [lastTargetName, setLastTargetName] = useState('');
  const [giftTarget, setGiftTarget] = useState<any>(null); 
  
  // 🟢 PAYSTACK KEY STATE
  const [brandPaystackKey, setBrandPaystackKey] = useState<string>('pk_live_7985b67599e69e640b5f64f870c93b8c77208617'); // Fallback to Platform Key

  // NETWORK STATE
  const [fullNetwork, setFullNetwork] = useState<any[]>([]);
  const [isNetworkLoading, setIsNetworkLoading] = useState(false);
  
  const [fetchedBrandName, setFetchedBrandName] = useState<string>('');
  const mobileScrollRef = useRef<HTMLDivElement>(null);

  // 🟢 LOAD PAYSTACK SCRIPT
  useEffect(() => {
      if (!document.querySelector('script[src="https://js.paystack.co/v1/inline.js"]')) {
          const script = document.createElement('script');
          script.src = 'https://js.paystack.co/v1/inline.js';
          script.async = true;
          document.body.appendChild(script);
      }
  }, []);

  // 🟢 REAL-TIME LISTENER FOR INVENTORY/STOCK UPDATES
  useEffect(() => {
      if (isOpen && product?.id) {
          const productRef = doc(db, 'products', product.id);
          const unsubscribe = onSnapshot(productRef, (docSnap) => {
              if (docSnap.exists()) {
                  setLiveProduct({ id: docSnap.id, ...docSnap.data() });
              }
          });
          return () => unsubscribe();
      } else {
          setLiveProduct(product);
      }
  }, [isOpen, product?.id]);

  // 🟢 BRAND NAME & PAYSTACK KEY FETCHER
  useEffect(() => {
      const fetchBrandData = async () => {
          const data = liveProduct || product;
          if (!data) return;

          const bId = data.businessId; 
          let foundKey = false;

          if (bId) {
              try {
                  const brandDoc = await getDoc(doc(db, 'users', bId));
                  if (brandDoc.exists()) {
                      const bData = brandDoc.data();
                      setFetchedBrandName(bData.businessName || bData.brandName || bData.storeName || bData.displayName || bData.username || 'Unknown Brand');
                      
                      if (bData.paystack?.publicKey) {
                          setBrandPaystackKey(bData.paystack.publicKey);
                          foundKey = true;
                      }
                  }
              } catch (e) { console.error("Failed to fetch brand data", e); }
          }
          
          if (!fetchedBrandName) {
              setFetchedBrandName(data.businessName || data.brand || data.brandName || data.storeName || 'Unknown Brand');
          }

          if (!foundKey) {
              try {
                  const adminDoc = await getDoc(doc(db, 'admin', 'global_settings_id'));
                  if (adminDoc.exists() && adminDoc.data()?.paystack?.publicKey) {
                      setBrandPaystackKey(adminDoc.data().paystack.publicKey);
                  }
              } catch(e) {}
          }
      };
      if (isOpen) fetchBrandData();
  }, [liveProduct, product, isOpen, fetchedBrandName]);

  // 🟢 NETWORK SEARCH (For DMs and Gifts)
  useEffect(() => {
      if (actionMode && fullNetwork.length === 0) {
          const fetchNetwork = async () => {
              setIsNetworkLoading(true);
              try {
                  const q = query(collection(db, 'users'));
                  const snap = await getDocs(q);
                  const users = snap.docs
                      .map(doc => ({ id: doc.id, ...doc.data() }))
                      .filter((u: any) => !u.isBrand && u.role !== 'brand' && u.role !== 'business' && u.id !== auth.currentUser?.uid);
                  setFullNetwork(users);
              } catch (error) { console.error("Network Error:", error); } 
              finally { setIsNetworkLoading(false); }
          };
          fetchNetwork();
      }
  }, [actionMode, fullNetwork.length]);

  const searchResults = searchQuery 
      ? fullNetwork.filter(u => (u.displayName || u.username || '').toLowerCase().includes(searchQuery.toLowerCase()))
      : fullNetwork.slice(0, 15);

  useEffect(() => {
    if (product && isOpen) {
      setSelectedSize('');
      setSelectedColor('');
      setQuantity(1);
      setCurrentImageIndex(0);
      setActionMode(null);
      setSearchQuery('');
      setActionSuccess(false);
      setIsTransmitting(false);
      setIsProcessingPayment(false);
      setGiftTarget(null);
    }
  }, [product, isOpen]);

  const currentData = liveProduct || product;
  const displayBrand = fetchedBrandName || "Unknown Brand";
  
  let rawSizes: string[] = [];
  if (Array.isArray(currentData?.sizes)) {
      rawSizes = currentData.sizes.map(String);
  } else if (typeof currentData?.sizes === 'string') {
      rawSizes = currentData.sizes.split(',').map((s: string) => s.trim());
  } else if (currentData?.sizes && typeof currentData.sizes === 'object') {
      rawSizes = Object.keys(currentData.sizes);
  }
  
  if (rawSizes.length === 0) {
      if (Array.isArray(currentData?.stockVariants)) {
          rawSizes = currentData.stockVariants.map((v: any) => typeof v === 'string' ? v : (v.size || v.name || v.variant)).filter(Boolean);
      } else if (currentData?.stockVariants && typeof currentData.stockVariants === 'object') {
          rawSizes = Object.keys(currentData.stockVariants);
      } else if (Array.isArray(currentData?.variants)) {
          rawSizes = currentData.variants.map((v: any) => typeof v === 'string' ? v : (v.size || v.name || v.variant)).filter(Boolean);
      } else if (currentData?.variants && typeof currentData.variants === 'object') {
          rawSizes = Object.keys(currentData.variants);
      }
  }
  const availableSizes = [...new Set(rawSizes)].filter(s => s && s.trim() !== '' && s !== 'undefined');

  let rawColors: string[] = [];
  if (Array.isArray(currentData?.colors)) {
      rawColors = currentData.colors.map(String);
  } else if (typeof currentData?.colors === 'string') {
      rawColors = currentData.colors.split(',').map((s: string) => s.trim());
  } else if (Array.isArray(currentData?.colours)) {
      rawColors = currentData.colours.map(String);
  } else if (typeof currentData?.colours === 'string') {
      rawColors = currentData.colours.split(',').map((s: string) => s.trim());
  }
  const availableColors = [...new Set(rawColors)].filter(c => c && c.trim() !== '' && c !== 'undefined');

  const regularPrice = Number(currentData?.regularPrice) || Number(currentData?.price) || 0;
  const salePrice = Number(currentData?.salePrice) || 0;
  const isOnSale = currentData?.onSale || currentData?.isOnSale || (salePrice > 0 && salePrice < regularPrice);
  const displayPrice = isOnSale ? salePrice : regularPrice;
  const isGlobalSoldOut = currentData?.stock !== undefined && currentData.stock <= 0;
  const gallery = currentData?.images && currentData.images.length > 0 ? currentData.images : [currentData?.image];

  const getColorHex = (colorName: string) => {
      const standardColors: Record<string, string> = {
          'black': '#000000', 'white': '#FFFFFF', 'red': '#ef4444', 'blue': '#3b82f6', 
          'green': '#22c55e', 'yellow': '#eab308', 'purple': '#a855f7', 'pink': '#ec4899', 
          'gray': '#71717a', 'grey': '#71717a', 'brown': '#78716c', 'orange': '#f97316',
          'navy': '#1e3a8a', 'olive': '#166534', 'cream': '#fef3c7', 'beige': '#f5f5dc',
          'tan': '#d2b48c', 'maroon': '#800000', 'teal': '#008080'
      };
      return standardColors[colorName.toLowerCase().trim()] || '#3f3f46';
  };

  if (availableSizes.length > 0 && selectedSize && !availableSizes.includes(selectedSize)) setSelectedSize('');
  if (availableColors.length > 0 && selectedColor && !availableColors.includes(selectedColor)) setSelectedColor('');

  const triggerValidationUI = (msg: string) => {
    setValidationError(msg);
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 500);
    setTimeout(() => setValidationError(null), 3000);
  };

  const validateSelection = () => {
    if (availableSizes.length > 0 && !selectedSize) { 
        triggerValidationUI("SELECT SIZE CLASSIFICATION"); 
        return false; 
    }
    if (availableColors.length > 0 && !selectedColor) { 
        triggerValidationUI("SELECT COLOR VARIANT"); 
        return false; 
    }
    return true;
  };

  const handleAddToCartClick = () => { if (validateSelection()) onAddToCart(currentData, selectedSize || 'One Size', selectedColor || 'Standard', quantity); };
  
  const handleBuyNowClick = () => { 
    if (validateSelection()) {
        onBuyNow(currentData, selectedSize || 'One Size', selectedColor || 'Standard', quantity);
        onClose(); 
    }
  };

  // ============================================================================
  // 🟢 GATEKEEPER BOUNCER FOR SOCIAL ACTIONS
  // ============================================================================
  const handleProtectedAction = (type: 'dm' | 'request' | 'send_gift') => {
      // 1. If they are a guest, pass it to App.tsx to trigger the Members Only popup!
      if (!auth.currentUser) {
          if (type === 'dm') onShareToDM(currentData);
          if (type === 'request') onRequestGift(currentData);
          if (type === 'send_gift') onSendGift(currentData);
          return;
      }

      // 2. If they are logged in, open the local overlay normally
      if (type === 'send_gift' && !validateSelection()) return;
      setActionMode(type);
  };

  // ============================================================================
  // 🟢 EXTERNAL SHARE LOGIC (Updated for Firebase Interceptor Bot Redirect)
  // ============================================================================
  const handleExternalShare = async () => {
      // 🟢 Changed from `/?product=` to `/p/` so social media bots hit your Cloud Function!
      const shareUrl = `${window.location.origin}/p/${currentData.id}`;
      const shareText = `Check out ${currentData.name} on Scoop Hype!`;

      if (navigator.share) {
          try {
              await navigator.share({
                  title: currentData.name,
                  text: shareText,
                  url: shareUrl
              });
          } catch (err) {
              console.error("Share failed:", err);
          }
      } else {
          navigator.clipboard.writeText(shareUrl);
          triggerValidationUI("LINK COPIED TO CLIPBOARD");
      }
  };

  const handleScroll = () => {
      if(mobileScrollRef.current) {
          const index = Math.round(mobileScrollRef.current.scrollLeft / mobileScrollRef.current.offsetWidth);
          setCurrentImageIndex(index);
      }
  };

  const handleUserSelect = async (user: any) => {
      const currentUserId = auth.currentUser?.uid;
      if (!currentUserId) { triggerValidationUI("LOGIN REQUIRED TO TRANSMIT"); return; }

      if (actionMode === 'send_gift') {
          setGiftTarget(user);
          return;
      }

      setIsTransmitting(true);
      setLastTargetName(user.displayName || user.username || "Personnel");

      try {
          const chatId = [currentUserId, user.id].sort().join("_");
          const chatRef = doc(db, "chats", chatId);
          
          await setDoc(chatRef, {
              participants: [currentUserId, user.id],
              updatedAt: serverTimestamp(),
              hidden: { [currentUserId]: false, [user.id]: false },
              [`unreadCount.${user.id}`]: increment(1),
              lastMessage: actionMode === 'dm' ? `Shared an asset` : `Requested an asset`
          }, { merge: true });

          await addDoc(collection(db, "chats", chatId, "messages"), {
              senderId: currentUserId,
              product: {
                  id: currentData.id,
                  name: currentData.name,
                  image: gallery[0],
                  price: displayPrice
              }, 
              messageType: actionMode === 'dm' ? 'item_share' : 'gift_request',
              text: actionMode === 'dm' ? `Check out this asset` : `System Request: Asset Acquisition (ZAR ${displayPrice})`,
              createdAt: serverTimestamp()
          });
          
          setActionSuccess(true);
          setTimeout(() => {
              setActionMode(null);
              setActionSuccess(false);
              setSearchQuery('');
              setIsTransmitting(false);
          }, 2000);

      } catch (error) {
          console.error("Database Transmission Failed:", error);
          triggerValidationUI("TRANSMISSION FAILED");
          setIsTransmitting(false);
      }
  };

  // ============================================================================
  // 🟢 REAL PAYSTACK PAYMENT FOR GIFTS
  // ============================================================================
  const handlePayAndSendGift = () => {
      if (!(window as any).PaystackPop) {
          triggerValidationUI("PAYMENT GATEWAY LOADING...");
          return;
      }

      setIsProcessingPayment(true);

      const handler = (window as any).PaystackPop.setup({
          key: brandPaystackKey,
          email: auth.currentUser?.email || 'user@scoop.app',
          amount: displayPrice * quantity * 100, // Cents
          currency: 'ZAR',
          ref: `GIFT_${currentData.id}_${Date.now()}`,
          callback: async function(response: any) {
              if (response.status === 'success') {
                  await finalizeGiftSending(response.reference);
              } else {
                  triggerValidationUI("PAYMENT DECLINED");
                  setIsProcessingPayment(false);
              }
          },
          onClose: function() {
              setIsProcessingPayment(false);
          }
      });

      handler.openIframe();
  };

  // 🟢 FULFILL GIFT ORDER & DM POST-PAYMENT
  const finalizeGiftSending = async (reference: string) => {
      const currentUserId = auth.currentUser?.uid;
      if (!currentUserId || !giftTarget) return;

      try {
          // 1. Create the Order in the DB for the Brand
          await addDoc(collection(db, 'orders'), {
              userId: currentUserId, 
              receiverId: giftTarget.id, 
              businessId: currentData.businessId || 'unknown',
              items: [{ 
                  ...currentData, 
                  quantity, 
                  selectedSize: selectedSize || 'One Size', 
                  selectedColor: selectedColor || 'Standard' 
              }],
              baseSubtotal: displayPrice * quantity,
              total: displayPrice * quantity,
              shippingCost: 0,
              status: 'gift_pending_address', // Special Status
              paymentMethod: 'paystack',
              paymentReference: reference,
              isGift: true,
              createdAt: serverTimestamp()
          });

          // 2. DM the target user so they can claim it
          const chatId = [currentUserId, giftTarget.id].sort().join("_");
          await setDoc(doc(db, "chats", chatId), {
              participants: [currentUserId, giftTarget.id],
              updatedAt: serverTimestamp(),
              hidden: { [currentUserId]: false, [giftTarget.id]: false },
              [`unreadCount.${giftTarget.id}`]: increment(1),
              lastMessage: `Sent a gift`
          }, { merge: true });

          await addDoc(collection(db, "chats", chatId, "messages"), {
              senderId: currentUserId,
              product: {
                  id: currentData.id,
                  name: currentData.name,
                  image: gallery[0],
                  price: displayPrice,
                  size: selectedSize || 'One Size',
                  color: selectedColor || 'Standard'
              }, 
              messageType: 'gift_sent',
              text: `Gift Transmitted: Asset Acquisition (ZAR ${displayPrice * quantity}). Reply with your shipping address to claim!`,
              createdAt: serverTimestamp()
          });
          
          setLastTargetName(giftTarget.displayName || giftTarget.username);
          setActionSuccess(true);
          setIsProcessingPayment(false);
          
          setTimeout(() => {
              setActionMode(null);
              setActionSuccess(false);
              setSearchQuery('');
              setGiftTarget(null);
          }, 3000);

      } catch (error) {
          console.error("Database Transmission Failed:", error);
          triggerValidationUI("TRANSACTION LOG FAILED");
          setIsProcessingPayment(false);
      }
  };

  const ActionOverlay = () => (
      <div className="absolute inset-0 bg-black z-[250] flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-200">
          {actionSuccess ? (
              <div className="flex-1 flex flex-col items-center justify-center p-12 text-center animate-in zoom-in-95 duration-300">
                  <CheckCircle size={64} className="text-white mb-6" strokeWidth={1.5} />
                  <h2 className="text-4xl lg:text-5xl font-black uppercase italic tracking-tighter text-white">SENT</h2>
                  <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.3em] mt-3">Target: {lastTargetName}</p>
              </div>
          ) : giftTarget ? (
              <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12 text-center animate-in slide-in-from-right-4">
                  <Gift size={48} className="text-white mb-6" strokeWidth={1.5} />
                  <h2 className="text-2xl lg:text-3xl font-black uppercase italic tracking-tighter text-white mb-2">Confirm Gift</h2>
                  <p className="font-mono text-xs text-zinc-400 uppercase tracking-widest mb-8 leading-relaxed">
                      Transmitting <span className="text-white">{quantity}x {currentData.name}</span><br/>
                      To: <span className="text-white">@{giftTarget.username}</span>
                  </p>
                  
                  <div className="w-full max-w-sm bg-zinc-950 border border-zinc-800 p-6 mb-8 rounded-2xl lg:rounded-none">
                      <div className="flex justify-between items-center mb-2">
                          <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Asset</span>
                          <span className="text-xs font-mono text-white">{formatCurrency(displayPrice)}</span>
                      </div>
                      <div className="flex justify-between items-center mb-4 pb-4 border-b border-zinc-900">
                          <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Quantity</span>
                          <span className="text-xs font-mono text-white">x{quantity}</span>
                      </div>
                      <div className="flex justify-between items-center">
                          <span className="text-xs font-black text-white uppercase tracking-widest">Total Pay</span>
                          <span className="text-lg font-mono text-white">{formatCurrency(displayPrice * quantity)}</span>
                      </div>
                  </div>

                  <div className="flex gap-4 w-full max-w-sm">
                      <button disabled={isProcessingPayment} onClick={() => setGiftTarget(null)} className="flex-1 py-4 border border-zinc-800 text-zinc-400 font-black uppercase tracking-widest text-[10px] hover:text-white transition-colors rounded-full lg:rounded-none disabled:opacity-50">Cancel</button>
                      <button disabled={isProcessingPayment} onClick={handlePayAndSendGift} className="flex-[2] py-4 bg-white text-black font-black uppercase tracking-widest text-[10px] hover:bg-zinc-200 transition-colors rounded-full lg:rounded-none shadow-xl flex items-center justify-center gap-2">
                          {isProcessingPayment ? <Loader2 size={16} className="animate-spin" /> : 'Pay & Send'}
                      </button>
                  </div>
              </div>
          ) : (
              <>
                  <div className="p-6 lg:p-12 border-b border-zinc-900 flex items-center justify-between sticky top-0 bg-black">
                      <div className="flex items-center gap-4">
                          <button onClick={() => setActionMode(null)} className="w-10 h-10 flex items-center justify-center border border-zinc-800 text-white hover:bg-white hover:text-black transition-colors rounded-full lg:rounded-none bg-zinc-950 active:scale-95">
                              <ArrowLeft size={20} strokeWidth={2.5} />
                          </button>
                          <h2 className="text-xl lg:text-3xl font-black uppercase italic tracking-tighter text-white leading-none">
                              {actionMode === 'dm' ? 'TRANSMIT TO' : actionMode === 'request' ? 'REQUEST FROM' : 'GIFT TO'}
                          </h2>
                      </div>
                  </div>

                  <div className="p-6 lg:p-12 flex-1 overflow-y-auto">
                      <div className="flex items-center gap-4 border-b-2 border-white pb-4 mb-8">
                          <Search size={24} className="text-zinc-500" />
                          <input 
                              autoFocus
                              placeholder="SEARCH USERNAME..." 
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="w-full bg-transparent text-xl lg:text-2xl font-black uppercase tracking-widest text-white focus:outline-none placeholder:text-zinc-800"
                          />
                      </div>

                      {isNetworkLoading ? (
                          <div className="flex flex-col items-center py-20 text-zinc-500">
                              <Loader2 size={40} strokeWidth={3} className="animate-spin mb-4" />
                              <span className="font-mono text-[10px] uppercase tracking-widest">Accessing Directory...</span>
                          </div>
                      ) : searchResults.length === 0 ? (
                          <div className="text-center py-20 text-zinc-600 font-mono text-[10px] uppercase tracking-widest">
                              No personnel found.
                          </div>
                      ) : (
                          <div className="space-y-3">
                              {searchResults.map((user) => (
                                  <button 
                                      key={user.id}
                                      onClick={() => handleUserSelect(user)}
                                      disabled={isTransmitting}
                                      className="w-full p-5 border-2 border-zinc-900 bg-zinc-950 flex items-center gap-4 hover:bg-white hover:border-white group transition-colors rounded-[2rem] lg:rounded-none text-left disabled:opacity-50"
                                  >
                                      <div className="w-12 h-12 border border-zinc-800 flex items-center justify-center grayscale group-hover:border-black overflow-hidden shrink-0 bg-black rounded-full lg:rounded-none text-zinc-500 font-bold uppercase text-lg">
                                          {user.photoURL || user.profileImage ? <img src={user.photoURL || user.profileImage} className="w-full h-full object-cover" alt="Avatar" /> : (user.displayName || 'U').charAt(0)}
                                      </div>
                                      <div className="flex-1">
                                          <h3 className="font-black text-lg uppercase tracking-widest leading-none text-white group-hover:text-black mb-1">
                                              {user.displayName || user.username || 'Personnel'}
                                          </h3>
                                          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-600 group-hover:text-zinc-800">
                                              @{user.username || 'user'}
                                          </p>
                                      </div>
                                      {isTransmitting && <Loader2 size={16} className="animate-spin text-zinc-500 group-hover:text-black" />}
                                  </button>
                              ))}
                          </div>
                      )}
                  </div>
              </>
          )}
      </div>
  );

  if (!isOpen || !currentData) return null;

  return (
    <div className={`fixed inset-0 z-[200] bg-black text-white overflow-hidden flex items-center justify-center font-sans ${isShaking ? 'animate-shake' : ''}`}>
        
        {validationError && (
            <div className="fixed top-24 lg:top-12 left-1/2 -translate-x-1/2 z-[300] bg-red-600 text-white px-6 py-3 rounded-full flex items-center gap-3 shadow-2xl animate-in slide-in-from-top-4 duration-300">
                <AlertTriangle size={18} strokeWidth={3} />
                <span className="font-black text-[10px] uppercase tracking-[0.2em]">{validationError}</span>
            </div>
        )}

        {/* =========================================================================
            📱 MOBILE VIEW (< 1024px)
        ========================================================================= */}
        <div className="lg:hidden flex flex-col w-full h-full relative overflow-y-auto pb-24">
            
            <div className="fixed top-0 left-0 right-0 z-40 p-4 pt-safe flex justify-between items-center bg-gradient-to-b from-black/70 to-transparent pointer-events-none">
                <button onClick={onClose} className="w-10 h-10 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/30 pointer-events-auto shadow-xl active:scale-90 transition-transform">
                    <ArrowLeft size={20}/>
                </button>
                <div className="flex gap-2 pointer-events-auto">
                    {/* 🟢 NEW: Mobile External Share Top Header Button */}
                    <button onClick={handleExternalShare} className="w-10 h-10 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/30 shadow-xl active:scale-90 transition-all">
                        <Share2 size={16} />
                    </button>
                    <button onClick={onToggleWishlist} className={`w-10 h-10 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center border shadow-xl active:scale-90 transition-all ${isWishlisted ? 'text-white border-white bg-white/20' : 'text-white border-white/30'}`}>
                        <Heart size={20} fill={isWishlisted ? "currentColor" : "none"} />
                    </button>
                </div>
            </div>

            <div className="relative h-[60vh] bg-zinc-950 border-b border-zinc-800 shrink-0">
                <div ref={mobileScrollRef} onScroll={handleScroll} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide h-full w-full">
                    {gallery.map((img: string, i: number) => (
                        <img key={i} src={img} loading="lazy" decoding="async" className={`w-full h-full object-cover snap-center flex-shrink-0 ${isGlobalSoldOut ? 'grayscale opacity-50' : ''}`} alt={`${currentData.name} ${i}`} />
                    ))}
                </div>
                {gallery.length > 1 && (
                    <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2 z-10">
                        {gallery.map((_: any, i: number) => (<div key={i} className={`h-1.5 rounded-full transition-all ${currentImageIndex === i ? 'bg-white w-6' : 'bg-white/30 w-1.5'}`} />))}
                    </div>
                )}
                {isGlobalSoldOut && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-white text-black font-black px-8 py-2 uppercase tracking-[0.3em] text-xl border-4 border-black shadow-2xl rounded-full">Sold Out</div>
                    </div>
                )}
            </div>
            
            <div className="flex-1 bg-black flex flex-col p-6 z-10 relative">
                {actionMode && <ActionOverlay />}

                <div className="flex justify-between items-start mb-6">
                    <div>
                        <button onClick={() => { onClose(); onViewBrand(currentData.businessId); }} className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest hover:text-white transition-colors mb-2 block">
                            [ {displayBrand} ]
                        </button>
                        <h1 className="text-3xl font-black text-white uppercase italic tracking-tighter leading-[0.9]">{currentData.name}</h1>
                    </div>
                    
                    <div className="flex flex-col items-end">
                        <div className="flex items-center gap-2">
                            {isOnSale && <div className="bg-white text-black px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest rounded-sm">SALE</div>}
                            <div className="text-xl font-mono text-white tracking-tighter">{formatCurrency(displayPrice)}</div>
                        </div>
                        {isOnSale && <div className="text-xs font-mono text-zinc-500 line-through mt-1">{formatCurrency(regularPrice)}</div>}
                    </div>
                </div>
                
                <div className="mb-6 pt-4 border-t border-zinc-800">
                    <div className="flex justify-between items-center mb-3">
                        <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Dimensions</p>
                        <button onClick={onOpenSizeGuide} className="text-zinc-500 text-[10px] font-mono uppercase tracking-widest hover:text-white transition-colors flex items-center gap-1"><Ruler size={12}/> Size Guide</button>
                    </div>
                    {availableSizes.length > 0 ? (
                        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide snap-x">
                            {availableSizes.map((s: string) => (
                                <button 
                                    key={s} 
                                    onClick={() => setSelectedSize(s)} 
                                    className={`h-12 min-w-[3rem] px-4 border rounded-2xl snap-center shrink-0 text-xs font-black uppercase tracking-widest transition-all ${selectedSize === s ? 'bg-white text-black border-white shadow-[4px_4px_0px_0px_rgba(255,255,255,0.2)]' : 'border-zinc-800 text-zinc-400 hover:border-zinc-500'}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="h-12 border border-zinc-800 border-dashed rounded-2xl flex items-center justify-center text-zinc-600 text-[10px] font-black uppercase tracking-widest bg-zinc-950/50">
                            Standard Fit / One Size
                        </div>
                    )}
                </div>

                {availableColors.length > 0 && (
                    <div className="mb-8">
                        <div className="flex justify-between items-center mb-3">
                            <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Color Variant</p>
                        </div>
                        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide snap-x">
                            {availableColors.map((c: string) => (
                                <button 
                                    key={c} 
                                    onClick={() => setSelectedColor(c)} 
                                    className={`h-12 px-4 border rounded-2xl snap-center shrink-0 flex items-center gap-2 text-xs font-black uppercase tracking-widest transition-all ${selectedColor === c ? 'bg-white text-black border-white shadow-[4px_4px_0px_0px_rgba(255,255,255,0.2)]' : 'border-zinc-800 text-zinc-400 hover:border-zinc-500'}`}
                                >
                                    <div className={`w-4 h-4 rounded-full border ${selectedColor === c ? 'border-zinc-300' : 'border-zinc-700'}`} style={{ backgroundColor: getColorHex(c) }} />
                                    {c}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                
                <div className="mb-8 flex items-center justify-between bg-zinc-950 p-4 border border-zinc-800 rounded-2xl">
                    <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Quantity</span>
                    <div className="flex items-center gap-6">
                        <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-8 h-8 rounded-full bg-zinc-900 text-white flex items-center justify-center border border-zinc-700 active:bg-zinc-800 active:scale-95 transition-all"><Minus size={14}/></button>
                        <span className="font-mono text-white text-sm w-4 text-center">{quantity}</span>
                        <button onClick={() => setQuantity(quantity + 1)} disabled={quantity >= (currentData.stock || 99)} className="w-8 h-8 rounded-full bg-zinc-900 text-white flex items-center justify-center border border-zinc-700 active:bg-zinc-800 active:scale-95 disabled:opacity-50 transition-all"><Plus size={14}/></button>
                    </div>
                </div>

                <div className="mt-auto flex flex-col gap-3">
                    <button onClick={handleAddToCartClick} disabled={isGlobalSoldOut} className="w-full py-5 border border-zinc-700 bg-black text-white font-black text-xs uppercase tracking-[0.2em] rounded-full hover:border-white transition-colors disabled:opacity-50 shadow-lg">
                        {currentData?.isPreorder ? 'Pre-Order to Cart' : 'Add to Cart'}
                    </button>
                    <button onClick={handleBuyNowClick} disabled={isGlobalSoldOut} className="w-full py-5 bg-white text-black font-black text-xs uppercase tracking-[0.2em] hover:bg-zinc-200 transition-colors disabled:opacity-50 rounded-full shadow-xl">
                        {isGlobalSoldOut ? 'Sold Out' : currentData?.isPreorder ? 'Secure Pre-Order' : 'Acquire Now'}
                    </button>
                    {currentData?.isPreorder && (
                        <p className="text-center text-amber-500 font-mono text-[9px] uppercase tracking-widest mt-1">
                            *This is a pre-order. {currentData?.fulfillmentDate || 'Shipping details TBA.'}
                        </p>
                    )}
                </div>

                <div className="mt-10 pt-8 border-t border-zinc-800">
                    <p className="text-zinc-400 font-mono text-[10px] uppercase tracking-widest mb-6 leading-relaxed">{currentData.description}</p>
                    
                    <div className="grid grid-cols-2 gap-3">
                        <button onClick={() => handleProtectedAction('dm')} className="col-span-2 flex items-center justify-between p-4 border border-zinc-800 text-zinc-400 font-black uppercase tracking-widest text-[10px] hover:text-white hover:border-zinc-500 transition-colors rounded-2xl bg-zinc-950 group">
                            <span className="flex items-center gap-2"><Send size={14} /> Transmit to Network</span>
                            <ArrowRight size={14} />
                        </button>

                        {/* 🟢 NEW: Mobile External Share Grid Button */}
                        <button onClick={handleExternalShare} className="col-span-2 flex items-center justify-between p-4 border border-zinc-800 text-zinc-400 font-black uppercase tracking-widest text-[10px] hover:text-white hover:border-zinc-500 transition-colors rounded-2xl bg-zinc-950 group">
                            <span className="flex items-center gap-2"><LinkIcon size={14} /> Share Externally</span>
                            <ArrowRight size={14} />
                        </button>

                        <button onClick={() => handleProtectedAction('request')} className="flex items-center justify-center gap-2 p-4 border border-zinc-800 text-zinc-400 font-black uppercase tracking-widest text-[10px] hover:text-white hover:border-zinc-500 transition-colors rounded-2xl bg-zinc-950 group">
                            <Gift size={14} className="group-hover:scale-110 transition-transform" /> Ask
                        </button>
                        <button onClick={() => handleProtectedAction('send_gift')} className="flex items-center justify-center gap-3 p-4 border border-zinc-800 text-zinc-400 font-black uppercase tracking-widest text-[10px] hover:text-white hover:border-zinc-500 transition-colors rounded-2xl bg-zinc-950 group">
                            <Gift size={14} className="group-hover:scale-110 transition-transform" /> Send Gift
                        </button>
                        <button onClick={() => onOpenAI(currentData)} className="col-span-2 mt-2 flex items-center justify-between p-4 bg-zinc-950 border border-purple-900 text-purple-400 font-black uppercase tracking-widest text-[10px] hover:bg-purple-900/30 hover:text-purple-300 transition-colors rounded-full mb-8">
                            <span className="flex items-center gap-2"><Sparkles size={14} /> Open AI Stylist</span>
                            <Sparkles size={14} />
                        </button>

                        <div className="flex gap-8 text-[10px] font-black uppercase tracking-widest text-zinc-600 justify-center col-span-2">
                            <span className="flex items-center gap-2"><CheckCircle size={14}/> Verified Auth</span>
                            {currentData?.isPreorder ? (
                                <span className="flex items-center gap-2 text-amber-600"><Clock size={14}/> Made to Order</span>
                            ) : (
                                <span className="flex items-center gap-2"><Truck size={14}/> Dispatch 24H</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* =========================================================================
            🖥️ DESKTOP VIEW (>= 1024px)
        ========================================================================= */}
        <div className="hidden lg:grid grid-cols-12 w-full h-full">
            
            <div className="col-span-7 bg-zinc-950 border-r border-zinc-900 relative flex flex-col h-full group">
                <div className="flex-1 relative overflow-hidden">
                    <img src={gallery[currentImageIndex]} loading="lazy" decoding="async" className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${isGlobalSoldOut ? 'grayscale opacity-50' : ''}`} alt={currentData.name} />
                </div>
                
                {gallery.length > 1 && (
                    <>
                        <button onClick={() => setCurrentImageIndex((prev) => (prev === 0 ? gallery.length - 1 : prev - 1))} className="absolute left-6 top-1/2 -translate-y-1/2 p-5 bg-black/80 hover:bg-white hover:text-black text-white rounded-none border border-zinc-800 transition-colors opacity-0 group-hover:opacity-100 backdrop-blur-sm"><ChevronLeft size={28} strokeWidth={2}/></button>
                        <button onClick={() => setCurrentImageIndex((prev) => (prev === gallery.length - 1 ? 0 : prev + 1))} className="absolute right-6 top-1/2 -translate-y-1/2 p-5 bg-black/80 hover:bg-white hover:text-black text-white rounded-none border border-zinc-800 transition-colors opacity-0 group-hover:opacity-100 backdrop-blur-sm"><ChevronRight size={28} strokeWidth={2}/></button>
                        
                        <div className="absolute bottom-12 left-0 right-0 flex justify-center gap-4 p-4">
                            {gallery.map((img: string, i: number) => (
                                <button key={i} onClick={() => setCurrentImageIndex(i)} className={`w-16 h-20 border-2 transition-all rounded-none overflow-hidden ${currentImageIndex === i ? 'border-white scale-110 shadow-2xl' : 'border-zinc-800 opacity-50 hover:opacity-100 hover:border-zinc-500'}`}>
                                    <img src={img} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale hover:grayscale-0" />
                                </button>
                            ))}
                        </div>
                    </>
                )}

                {isGlobalSoldOut && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-white text-black font-black px-16 py-6 uppercase tracking-[0.4em] text-5xl border-8 border-black shadow-2xl rotate-[-5deg] rounded-none">Sold Out</div>
                    </div>
                )}
                
                <button onClick={onClose} className="absolute top-8 left-8 p-4 bg-black border border-zinc-800 hover:bg-white hover:text-black text-zinc-500 transition-colors rounded-none shadow-xl"><ArrowLeft size={24} strokeWidth={2.5}/></button>
            </div>

            <div className="col-span-5 bg-black relative flex flex-col h-full overflow-hidden">
                {actionMode && <ActionOverlay />}
                
                <div className="p-12 overflow-y-auto flex-1 flex flex-col">
                    
                    <div className="flex items-center justify-between mb-12 border-b border-zinc-800 pb-4">
                        <div className="flex items-center gap-2 text-zinc-500 text-[10px] font-mono uppercase tracking-[0.3em]">
                            <span>Archive</span> <ChevronRight size={12}/> 
                            <span className="text-white cursor-pointer hover:underline underline-offset-4" onClick={() => { onClose(); onViewBrand(currentData.businessId); }}>{displayBrand}</span> 
                        </div>
                        <div className="flex gap-2">
                            {/* 🟢 NEW: Desktop External Share Header Button */}
                            <button onClick={handleExternalShare} className="p-3 border border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-500 transition-colors rounded-none">
                                <Share2 size={16} strokeWidth={2.5} />
                            </button>
                            <button onClick={onToggleWishlist} className={`p-3 border transition-colors rounded-none ${isWishlisted ? 'text-white border-white bg-white/10' : 'text-zinc-500 border-zinc-800 hover:text-white hover:border-zinc-500'}`}>
                                <Heart size={16} strokeWidth={2.5} fill={isWishlisted ? "currentColor" : "none"} />
                            </button>
                        </div>
                    </div>

                    <h1 className="text-6xl xl:text-7xl font-black italic uppercase tracking-tighter leading-[0.85] mb-6 text-white">{currentData.name}</h1>
                    
                    <div className="flex items-end gap-4 mb-12">
                        {isOnSale && <div className="bg-white text-black px-3 py-1 text-xs font-black uppercase tracking-widest rounded-none mb-1">SALE</div>}
                        <div className="text-3xl font-mono text-white tracking-tighter">{formatCurrency(displayPrice)}</div>
                        {isOnSale && <div className="text-xl font-mono text-zinc-500 line-through mb-0.5">{formatCurrency(regularPrice)}</div>}
                    </div>

                    <div className="space-y-10 mb-12 flex-1">
                        <div className="flex justify-between items-center border-b border-zinc-800 pb-6">
                            <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Asset Quantity</span>
                            <div className="flex items-center gap-6">
                                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-12 h-12 border border-zinc-700 rounded-none flex items-center justify-center hover:bg-white hover:text-black transition-colors"><Minus size={16}/></button>
                                <span className="font-mono text-xl font-bold w-8 text-center">{quantity}</span>
                                <button onClick={() => setQuantity(quantity + 1)} disabled={quantity >= (currentData.stock || 99)} className="w-12 h-12 border border-zinc-700 rounded-none flex items-center justify-center hover:bg-white hover:text-black disabled:opacity-50 transition-colors"><Plus size={16}/></button>
                            </div>
                        </div>

                        <div>
                            <div className="flex justify-between items-center mb-4">
                                <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Dimensions</span>
                                <button onClick={onOpenSizeGuide} className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-white underline underline-offset-4 flex items-center gap-2"><Ruler size={12}/> Size Guide</button>
                            </div>
                            {availableSizes.length > 0 ? (
                                <div className="grid grid-cols-4 gap-4">
                                    {availableSizes.map((s: string) => (
                                        <button 
                                            key={s} 
                                            onClick={() => setSelectedSize(s)} 
                                            className={`h-16 border-2 rounded-none flex items-center justify-center font-black text-sm uppercase tracking-widest transition-all ${selectedSize === s ? 'bg-white text-black border-white shadow-[6px_6px_0px_0px_rgba(255,255,255,0.2)]' : 'border-zinc-800 text-zinc-500 hover:border-zinc-500 hover:text-white'}`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="h-16 border border-zinc-800 border-dashed rounded-none flex items-center justify-center text-zinc-600 text-xs font-black uppercase tracking-widest bg-zinc-950/50">
                                    Standard Fit / One Size
                                </div>
                            )}
                        </div>

                        {availableColors.length > 0 && (
                            <div>
                                <div className="flex justify-between items-center mb-4">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Color Variant</span>
                                </div>
                                <div className="grid grid-cols-4 gap-4">
                                    {availableColors.map((c: string) => (
                                        <button 
                                            key={c} 
                                            onClick={() => setSelectedColor(c)} 
                                            className={`h-16 border-2 rounded-none flex flex-col items-center justify-center gap-2 font-black text-[10px] uppercase tracking-widest transition-all ${selectedColor === c ? 'bg-white text-black border-white shadow-[6px_6px_0px_0px_rgba(255,255,255,0.2)]' : 'border-zinc-800 text-zinc-500 hover:border-zinc-500 hover:text-white'}`}
                                        >
                                            <div className={`w-4 h-4 rounded-full border ${selectedColor === c ? 'border-zinc-400' : 'border-zinc-600'}`} style={{ backgroundColor: getColorHex(c) }} />
                                            {c}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col gap-2 mb-12 shrink-0">
                        <div className="grid grid-cols-2 gap-4">
                            <button onClick={handleAddToCartClick} disabled={isGlobalSoldOut} className="py-6 border-2 border-zinc-700 bg-black text-white font-black text-xs uppercase tracking-[0.2em] hover:border-white transition-colors disabled:opacity-50 rounded-none">
                                {currentData?.isPreorder ? 'Pre-Order to Cart' : 'Add to Cart'}
                            </button>
                            <button onClick={handleBuyNowClick} disabled={isGlobalSoldOut} className="py-6 bg-white text-black font-black text-xs uppercase tracking-[0.2em] hover:bg-zinc-200 transition-colors disabled:opacity-50 rounded-none shadow-2xl">
                                {isGlobalSoldOut ? 'Sold Out' : currentData?.isPreorder ? 'Secure Pre-Order' : 'Acquire Now'}
                            </button>
                        </div>
                        {currentData?.isPreorder && (
                            <p className="text-amber-500 font-mono text-[10px] uppercase tracking-widest text-center mt-2 border border-amber-500/30 bg-amber-500/10 py-2">
                                ⚠️ PRE-ORDER SECURED: {currentData?.fulfillmentDate || 'Estimated shipping TBA.'}
                            </p>
                        )}
                    </div>

                    <div className="border-t border-zinc-800 pt-8 mt-auto shrink-0 space-y-6">
                        {currentData.description && (
                            <div>
                                <h4 className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-2">Description</h4>
                                <p className="text-zinc-300 font-mono text-[10px] uppercase tracking-widest leading-relaxed">{currentData.description}</p>
                            </div>
                        )}
                        {currentData.materials && (
                            <div>
                                <h4 className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-2">Materials</h4>
                                <p className="text-zinc-300 font-mono text-[10px] uppercase tracking-widest leading-relaxed">{currentData.materials}</p>
                            </div>
                        )}
                        {(currentData.careInstructions || currentData.care) && (
                            <div>
                                <h4 className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-2">Care</h4>
                                <p className="text-zinc-300 font-mono text-[10px] uppercase tracking-widest leading-relaxed">{currentData.careInstructions || currentData.care}</p>
                            </div>
                        )}
                        
                        {/* 🟢 NEW: Desktop Action Buttons Grid (Updated to hold 4 buttons) */}
                        <div className="grid grid-cols-4 gap-4 pt-6">
                            <button onClick={() => handleProtectedAction('dm')} className="col-span-2 flex items-center justify-between p-4 border border-zinc-800 text-zinc-400 font-black uppercase tracking-widest text-[10px] hover:text-white hover:border-zinc-500 transition-colors rounded-none bg-zinc-950 group">
                                <span className="flex items-center gap-2"><Send size={14} /> Transmit</span>
                                <ArrowRight size={14} />
                            </button>

                            <button onClick={handleExternalShare} className="col-span-2 flex items-center justify-between p-4 border border-zinc-800 text-zinc-400 font-black uppercase tracking-widest text-[10px] hover:text-white hover:border-zinc-500 transition-colors rounded-none bg-zinc-950 group">
                                <span className="flex items-center gap-2"><LinkIcon size={14} /> Copy Link</span>
                                <ArrowRight size={14} />
                            </button>

                            <button onClick={() => handleProtectedAction('request')} className="col-span-2 flex items-center justify-center gap-2 p-4 border border-zinc-800 text-zinc-400 font-black uppercase tracking-widest text-[10px] hover:text-white hover:border-zinc-500 transition-colors rounded-none bg-zinc-950 group">
                                <Gift size={14} className="group-hover:scale-110 transition-transform" /> Ask
                            </button>
                            
                            <button onClick={() => handleProtectedAction('send_gift')} className="col-span-2 flex items-center justify-center gap-3 p-4 border border-zinc-800 text-zinc-400 font-black uppercase tracking-widest text-[10px] hover:text-white hover:border-zinc-500 transition-colors rounded-none bg-zinc-950 group">
                                <Gift size={14} className="group-hover:scale-110 transition-transform" /> Send Gift
                            </button>
                            
                            <button onClick={() => onOpenAI(currentData)} className="col-span-4 mt-2 flex items-center justify-between p-4 bg-zinc-950 border border-purple-900 text-purple-400 font-black uppercase tracking-widest text-[10px] hover:bg-purple-900/30 hover:text-purple-300 transition-colors rounded-none mb-8">
                                <span className="flex items-center gap-2"><Sparkles size={14} /> Open AI Stylist</span>
                                <Sparkles size={14} />
                            </button>

                            <div className="flex gap-8 text-[10px] font-black uppercase tracking-widest text-zinc-600 justify-center col-span-4">
                                <span className="flex items-center gap-2"><CheckCircle size={14}/> Verified Auth</span>
                                {currentData?.isPreorder ? (
                                    <span className="flex items-center gap-2 text-amber-600"><Clock size={14}/> Made to Order</span>
                                ) : (
                                    <span className="flex items-center gap-2"><Truck size={14}/> Dispatch 24H</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* 🟢 CSS FOR VALIDATION SHAKE */}
        <style dangerouslySetInnerHTML={{__html: `
            @keyframes shake {
                0%, 100% { transform: translateX(0); }
                25% { transform: translateX(-8px); }
                75% { transform: translateX(8px); }
            }
            .animate-shake { animation: shake 0.15s ease-in-out 0s 2; }
        `}} />
    </div>
  );
};